import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { blogService } from "@/services/blog";
import BlogCard from "@/components/blog/BlogCard";
import BlogCategoryFilter from "@/components/blog/BlogCategoryFilter";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { SimpleComingSoon } from "./ComingSoon";
import { isComingSoonEnabled } from "@/utils/featureFlags";

const POSTS_PER_PAGE = 9;

const Blog: React.FC = () => {
  if (isComingSoonEnabled()) return <SimpleComingSoon />;

  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.get("tags")?.split(",").filter(Boolean) || []
  );
  
  // Get current page from URL
  const currentPage = Number(searchParams.get("page")) || 1;

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ["blogCategories"],
    queryFn: blogService.getCategories,
  });

  // Fetch posts with filters
  const { data: postsData, isLoading } = useQuery({
    queryKey: ["blogPosts", categorySlug, searchQuery, selectedTags, currentPage],
    queryFn: () => blogService.getPosts({
      category: categorySlug,
      tag: selectedTags[0], // For now, only use first tag
      search: searchQuery,
      page: currentPage,
      limit: POSTS_PER_PAGE,
    }),
  });

  // Get current category
  const currentCategory = categorySlug
    ? categories?.find(cat => cat.slug === categorySlug)
    : null;

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set("q", searchQuery);
    } else {
      params.delete("q");
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  // Handle tag selection
  const handleTagClick = (tag: string) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    const params = new URLSearchParams(searchParams);
    if (newTags.length > 0) {
      params.set("tags", newTags.join(","));
    } else {
      params.delete("tags");
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  // Get all unique tags from posts
  const allTags = React.useMemo(() => {
    if (!postsData?.data) return [];
    const tags = new Set<string>();
    postsData.data.forEach(post => {
      post.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [postsData?.data]);

  return (
    <>
      <Helmet>
        <title>
          {currentCategory 
            ? `${currentCategory.name} Articles | SaunaDip Blog` 
            : "Blog | SaunaDip - Sauna and Cold Plunge Insights"}
        </title>
        <meta 
          name="description" 
          content={currentCategory 
            ? `Explore our articles about ${currentCategory.name.toLowerCase()} on SaunaDip's blog`
            : "Discover articles about contrast therapy, wellness, adventures and product updates from SaunaDip"} 
        />
      </Helmet>
      
      <div className="min-h-screen bg-sauna-beige py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-sauna-navy">
              {currentCategory ? `${currentCategory.name} Articles` : "Blog"}
            </h1>
            
            <p className="mt-1 text-gray-500">
              {currentCategory 
                ? `Explore our articles about ${currentCategory.name.toLowerCase()}`
                : "Insights, stories and updates from the world of contrast therapy"}
            </p>
            
            <div className="h-1 w-24 divider-gradient rounded-full mt-2"></div>
          </div>

          {/* Category Filter */}
          {categories && categories.length > 0 && (
            <BlogCategoryFilter categories={categories} />
          )}

          {/* Search and Filters */}
          <div className="mb-8 space-y-4 mt-6">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit">Search</Button>
              {(searchQuery || selectedTags.length > 0) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedTags([]);
                    setSearchParams({});
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </form>

            {/* Tags */}
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer hover:bg-sauna-orange/10",
                      selectedTags.includes(tag) && "bg-sauna-orange text-white"
                    )}
                    onClick={() => handleTagClick(tag)}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Posts Grid */}
          <section>
            {isLoading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-video w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : postsData?.data && postsData.data.length > 0 ? (
                <>
                  <ErrorBoundary>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {postsData.data.map((post) => {
                        return <BlogCard key={post.id} post={post} />;
                      })}
                    </div>
                  </ErrorBoundary>

                  {/* Render pagination if main posts are available and multiple pages exist */}
                  {postsData.last_page > 1 && (
                    <Pagination className="mt-8">
                      <PaginationContent>
                        {Array.from({ length: postsData.last_page }, (_, i) => i + 1).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                const params = new URLSearchParams(searchParams);
                                params.set("page", page.toString());
                                setSearchParams(params);
                              }}
                              isActive={currentPage === page}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                      </PaginationContent>
                    </Pagination>
                  )}
                </>
            ) : !isLoading && (!postsData?.data || postsData.data.length === 0) ? (
               <div className="text-center py-12">
                 <h3 className="text-lg font-medium text-sauna-navy">No articles found</h3>
                 <p className="mt-2 text-muted-foreground">
                   Try adjusting your search or filters to find what you're looking for.
                 </p>
               </div>
            ) : null}
          </section>
        </div>
      </div>
    </>
  );
};

export default Blog;
